import React, { useState, useEffect, useCallback } from 'react';
import './TokenRefreshBanner.css';
import { fetchTokenHealth } from '../../services/tokenHealth';
import { isAuthenticated } from '../../services/auth';

const TokenRefreshBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Check if error flag is already set on mount
    const hasError = localStorage.getItem('zerodha_auth_error') === 'true';
    if (hasError) {
      setShowBanner(true);
    }

    // Listen for auth error events
    const handleAuthError = () => {
      setShowBanner(true);
    };

    window.addEventListener('zerodha-auth-error', handleAuthError);

    return () => {
      window.removeEventListener('zerodha-auth-error', handleAuthError);
    };
  }, []);

  const handleRefreshToken = useCallback(async () => {
    setIsRefreshing(true);
    setErrorMessage('');

    try {
      const response = await fetch('/api/auth/refresh-token', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to refresh token');
      }

      const data = await response.json();

      if (data.success) {
        // Clear error flag
        localStorage.removeItem('zerodha_auth_error');

        // Show reconnecting message
        setErrorMessage('Token refreshed! Reconnecting services...');

        // Wait for backend to retry instrument sync (runs in background goroutine)
        // Give it 3 seconds to complete
        await new Promise(resolve => setTimeout(resolve, 3000));

        // Hide banner
        setShowBanner(false);

        // Reload the page to reinitialize frontend services
        window.location.reload();
      } else {
        throw new Error(data.message || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      setErrorMessage(error.message || 'Failed to refresh token. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Proactive token check on app start
  // If token was created before 8 AM today IST, auto-refresh once
  useEffect(() => {
    const checkTokenAge = async () => {
      try {
        const tokenData = await fetchTokenHealth();
        if (tokenData?.created_at) {
          const createdAt = new Date(tokenData.created_at);
          const now = new Date();

          // Get 8 AM today in local time (IST for India)
          const today8AM = new Date();
          today8AM.setHours(8, 0, 0, 0);

          // If current time is after 8 AM AND token was created before 8 AM today
          if (now >= today8AM && createdAt < today8AM) {
            console.log('Token created before 8 AM today, auto-refreshing...');
            setShowBanner(true);
            handleRefreshToken();
          }
        }
      } catch (error) {
        // Silently fail - don't show error for proactive check
        console.error('Error checking token health:', error);
      }
    };

    // Only check if user is authenticated
    if (isAuthenticated()) {
      checkTokenAge();
    }
  }, [handleRefreshToken]);

  const handleDismiss = () => {
    // Allow user to dismiss temporarily (will reappear on next error)
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="token-refresh-banner">
      <div className="banner-content">
        <div className="banner-icon">
          <i className="fas fa-exclamation-triangle"></i>
        </div>
        <div className="banner-message">
          <strong>Zerodha Session Expired</strong>
          <p>Your connection to Zerodha has expired. Click refresh to reconnect.</p>
          {errorMessage && (
            <p className={errorMessage.startsWith('Token refreshed!') ? 'success-text' : 'error-text'}>
              {errorMessage}
            </p>
          )}
        </div>
        <div className="banner-actions">
          <button
            className="refresh-button"
            onClick={handleRefreshToken}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Refreshing...
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt"></i> Refresh Token
              </>
            )}
          </button>
          <button
            className="dismiss-button"
            onClick={handleDismiss}
            disabled={isRefreshing}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TokenRefreshBanner;
